import type { SupabaseClient } from '@supabase/supabase-js';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { getLogger } from '@kit/shared/logger';
import { Result, ServiceError } from '@kit/shared/result';
import type { Database } from '@kit/supabase/database';

import type {
  Choreographer,
  CreateRepertoireInput,
  DancerRepertoireEntry,
  Piece,
  PieceOption,
  UpdateRepertoireInput,
} from './dancer-repertoire.schema';

export type {
  Choreographer,
  CreateRepertoireInput,
  DancerRepertoireEntry,
  Piece,
  PieceOption,
  UpdateRepertoireInput,
} from './dancer-repertoire.schema';

/**
 * Dancer Repertoire Service
 * Handles dance pieces and repertoire management
 */
export function createDancerRepertoireService(
  client: SupabaseClient<Database>,
) {
  return {
    /**
     * Get all active choreographers
     */
    async getChoreographers(): Promise<Result<Choreographer[]>> {
      const logger = await getLogger();

      try {
        const { data, error } = await fetchAllRows((from, to) =>
          client
            .from('choreographers')
            .select('*')
            .eq('is_active', true)
            .order('name')
            .order('id')
            .range(from, to),
        );

        if (error) {
          await logger.error(
            { name: 'get-choreographers-error', error: error.message },
            'Failed to fetch choreographers',
          );

          return Result.fail(
            new ServiceError(
              'FETCH_ERROR',
              `Failed to fetch choreographers: ${error.message}`,
              error,
            ),
          );
        }

        const choreographers: Choreographer[] = (data || []).map((c) => ({
          id: c.id,
          name: c.name,
          birthYear: c.birth_year,
          deathYear: c.death_year,
          nationality: c.nationality,
          style: c.style,
          biography: c.biography,
          imageUrl: c.image_url,
          isActive: c.is_active,
          createdAt: c.created_at,
          updatedAt: c.updated_at,
        }));

        return Result.ok(choreographers);
      } catch (err) {
        captureServiceError(
          { message: 'Failed to fetch choreographers', code: 'FETCH_ERROR' },
          { action: 'getChoreographers', step: 'fetch' },
        );
        return Result.fail(
          new ServiceError(
            'FETCH_ERROR',
            `Failed to fetch choreographers: ${err instanceof Error ? err.message : 'Unknown error'}`,
            err,
          ),
        );
      }
    },

    /**
     * Get all active pieces with optional filters
     */
    async getPieces(filters?: {
      style?: string;
      pieceType?: string;
      choreographerId?: string;
      genderRequirement?: string;
      search?: string;
    }): Promise<Result<PieceOption[]>> {
      const logger = await getLogger();

      try {
        let query = client
          .from('pieces')
          .select(
            `
            id,
            name,
            code,
            piece_type,
            style,
            difficulty_level,
            gender_requirement,
            music_composer,
            choreographer:choreographers(name)
          `,
          )
          .eq('is_active', true)
          .order('name');

        if (filters?.style) {
          query = query.eq('style', filters.style);
        }
        if (filters?.pieceType) {
          query = query.eq('piece_type', filters.pieceType);
        }
        if (filters?.choreographerId) {
          query = query.eq('choreographer_id', filters.choreographerId);
        }
        if (filters?.genderRequirement) {
          query = query.eq('gender_requirement', filters.genderRequirement);
        }
        if (filters?.search) {
          const escapedSearch = filters.search
            .replace(/%/g, '\\%')
            .replace(/_/g, '\\_');
          query = query.ilike('name', `%${escapedSearch}%`);
        }

        const orderedQuery = query.order('id');
        const { data, error } = await fetchAllRows((from, to) =>
          orderedQuery.range(from, to),
        );

        if (error) {
          await logger.error(
            { name: 'get-pieces-error', error: error.message },
            'Failed to fetch pieces',
          );

          return Result.fail(
            new ServiceError(
              'FETCH_ERROR',
              `Failed to fetch pieces: ${error.message}`,
              error,
            ),
          );
        }

        const pieces: PieceOption[] = (data || []).map(
          (p: {
            id: string;
            name: string;
            code: string | null;
            piece_type: string;
            style: string | null;
            difficulty_level: number | null;
            gender_requirement: string | null;
            music_composer: string | null;
            choreographer: { name: string } | null;
          }) => ({
            id: p.id,
            name: p.name,
            code: p.code,
            pieceType: p.piece_type,
            style: p.style,
            difficultyLevel: p.difficulty_level,
            genderRequirement: p.gender_requirement,
            musicComposer: p.music_composer,
            choreographerName: p.choreographer?.name || null,
          }),
        );

        return Result.ok(pieces);
      } catch (err) {
        captureServiceError(
          { message: 'Failed to fetch pieces', code: 'FETCH_ERROR' },
          { action: 'getPieces', step: 'fetch' },
        );
        return Result.fail(
          new ServiceError(
            'FETCH_ERROR',
            `Failed to fetch pieces: ${err instanceof Error ? err.message : 'Unknown error'}`,
            err,
          ),
        );
      }
    },

    /**
     * Get a single piece by ID with full details
     */
    async getPiece(pieceId: string): Promise<Result<Piece>> {
      const logger = await getLogger();

      try {
        const { data, error } = await client
          .from('pieces')
          .select(
            `
            *,
            choreographer:choreographers(*)
          `,
          )
          .eq('id', pieceId)
          .single();

        if (error) {
          await logger.error(
            { name: 'get-piece-error', pieceId, error: error.message },
            'Failed to fetch piece',
          );

          return Result.fail(
            new ServiceError(
              'FETCH_ERROR',
              `Failed to fetch piece: ${error.message}`,
              error,
            ),
          );
        }

        const choreographerData = data.choreographer as {
          id: string;
          name: string;
          birth_year: number | null;
          death_year: number | null;
          nationality: string | null;
          style: string | null;
          biography: string | null;
          image_url: string | null;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        } | null;
        const piece: Piece = {
          id: data.id,
          name: data.name,
          code: data.code,
          pieceType: data.piece_type,
          style: data.style,
          difficultyLevel: data.difficulty_level,
          choreographerId: data.choreographer_id,
          choreographer: choreographerData
            ? {
                id: choreographerData.id,
                name: choreographerData.name,
                birthYear: choreographerData.birth_year,
                deathYear: choreographerData.death_year,
                nationality: choreographerData.nationality,
                style: choreographerData.style,
                biography: choreographerData.biography,
                imageUrl: choreographerData.image_url,
                isActive: choreographerData.is_active,
                createdAt: choreographerData.created_at,
                updatedAt: choreographerData.updated_at,
              }
            : null,
          productionId: data.production_id,
          musicComposer: data.music_composer,
          musicTitle: data.music_title,
          durationSeconds: data.duration_seconds,
          genderRequirement: data.gender_requirement,
          requiresPointe: data.requires_pointe ?? false,
          requiresPartner: data.requires_partner ?? false,
          technicalRequirements: data.technical_requirements,
          costumeDescription: data.costume_description,
          description: data.description,
          videoUrl: data.video_url,
          notes: data.notes,
          isActive: data.is_active ?? true,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        };

        return Result.ok(piece);
      } catch (err) {
        captureServiceError(
          { message: 'Failed to fetch piece', code: 'FETCH_ERROR' },
          { action: 'getPiece', step: 'fetch' },
        );
        return Result.fail(
          new ServiceError(
            'FETCH_ERROR',
            `Failed to fetch piece: ${err instanceof Error ? err.message : 'Unknown error'}`,
            err,
          ),
        );
      }
    },

    /**
     * Get repertoire for a user
     */
    async getRepertoire(
      userId: string,
    ): Promise<Result<DancerRepertoireEntry[]>> {
      const logger = await getLogger();

      try {
        const { data, error } = await fetchAllRows((from, to) =>
          client
            .from('artist_repertoire')
            .select(
              `
            *,
            piece:pieces(
              id,
              name,
              code,
              piece_type,
              style,
              difficulty_level,
              music_composer,
              choreographer:choreographers(name)
            ),
            organization:organizations(name),
            event:events(title)
          `,
            )
            .eq('user_id', userId)
            .order('display_order', { ascending: true })
            .order('performance_date', { ascending: false })
            .order('id')
            .range(from, to),
        );

        if (error) {
          await logger.error(
            { name: 'get-repertoire-error', userId, error: error.message },
            'Failed to fetch repertoire',
          );

          return Result.fail(
            new ServiceError(
              'FETCH_ERROR',
              `Failed to fetch repertoire: ${error.message}`,
              error,
            ),
          );
        }

        // Type for the joined query result from Supabase
        type RepertoireQueryRow = {
          id: string;
          user_id: string;
          piece_id: string;
          performed_at_organization_id: string | null;
          performed_at_event_id: string | null;
          performance_date: string | null;
          performance_location: string | null;
          role_in_piece: string | null;
          notes: string | null;
          video_url: string | null;
          is_verified: boolean | null;
          hide_on_resume: boolean | null;
          display_order: number | null;
          created_at: string;
          updated_at: string;
          piece: {
            id: string;
            name: string;
            code: string | null;
            piece_type: string;
            style: string | null;
            difficulty_level: number | null;
            music_composer: string | null;
            choreographer: { name: string } | null;
          } | null;
          organization: { name: string | null } | null;
          event: { title: string | null } | null;
        };

        const repertoire: DancerRepertoireEntry[] = (
          (data || []) as unknown as RepertoireQueryRow[]
        ).map((r) => ({
          id: r.id,
          userId: r.user_id,
          pieceId: r.piece_id,
          piece: r.piece
            ? {
                id: r.piece.id,
                name: r.piece.name,
                code: r.piece.code,
                pieceType: r.piece.piece_type,
                style: r.piece.style,
                difficultyLevel: r.piece.difficulty_level,
                choreographerId: null,
                choreographer: r.piece.choreographer
                  ? {
                      id: '',
                      name: r.piece.choreographer.name,
                      birthYear: null,
                      deathYear: null,
                      nationality: null,
                      style: null,
                      biography: null,
                      imageUrl: null,
                      isActive: true,
                      createdAt: '',
                      updatedAt: '',
                    }
                  : null,
                productionId: null,
                musicComposer: r.piece.music_composer,
                musicTitle: null,
                durationSeconds: null,
                genderRequirement: null,
                requiresPointe: false,
                requiresPartner: false,
                technicalRequirements: null,
                costumeDescription: null,
                description: null,
                videoUrl: null,
                notes: null,
                isActive: true,
                createdAt: '',
                updatedAt: '',
              }
            : null,
          performedAtOrganizationId: r.performed_at_organization_id,
          organizationName: r.organization?.name || null,
          performedAtEventId: r.performed_at_event_id,
          eventName: r.event?.title || null,
          performanceDate: r.performance_date,
          performanceLocation: r.performance_location,
          roleInPiece: r.role_in_piece,
          notes: r.notes,
          videoUrl: r.video_url,
          isVerified: r.is_verified ?? false,
          hideOnResume: r.hide_on_resume ?? false,
          displayOrder: r.display_order ?? 0,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
        }));

        return Result.ok(repertoire);
      } catch (err) {
        captureServiceError(
          { message: 'Failed to fetch repertoire', code: 'FETCH_ERROR' },
          { action: 'getRepertoire', step: 'fetch' },
        );
        return Result.fail(
          new ServiceError(
            'FETCH_ERROR',
            `Failed to fetch repertoire: ${err instanceof Error ? err.message : 'Unknown error'}`,
            err,
          ),
        );
      }
    },

    /**
     * Get public repertoire for a user (visible on their profile)
     */
    async getPublicRepertoire(
      userId: string,
    ): Promise<Result<DancerRepertoireEntry[]>> {
      const result = await this.getRepertoire(userId);
      if (result.isFailure) {
        return result;
      }

      // Filter to only show non-hidden entries
      const publicEntries = result.getValue().filter((r) => !r.hideOnResume);
      return Result.ok(publicEntries);
    },

    /**
     * Add a piece to dancer's repertoire
     */
    async addToRepertoire(
      input: CreateRepertoireInput,
    ): Promise<Result<DancerRepertoireEntry>> {
      const logger = await getLogger();

      try {
        const {
          data: { user },
        } = await client.auth.getUser();

        if (!user) {
          return Result.fail(
            new ServiceError(
              'UNAUTHORIZED',
              'Must be logged in to add to repertoire',
            ),
          );
        }

        // Get the next display order
        const { data: existingRepertoire } = await client
          .from('artist_repertoire')
          .select('display_order')
          .eq('user_id', user.id)
          .order('display_order', { ascending: false })
          .limit(1);

        const nextOrder =
          existingRepertoire?.[0]?.display_order != null
            ? existingRepertoire[0].display_order + 1
            : 0;

        const { data, error } = await client
          .from('artist_repertoire')
          .insert({
            user_id: user.id,
            piece_id: input.pieceId,
            performed_at_organization_id:
              input.performedAtOrganizationId || null,
            performed_at_event_id: input.performedAtEventId || null,
            performance_date: input.performanceDate || null,
            performance_location: input.performanceLocation || null,
            role_in_piece: input.roleInPiece || null,
            notes: input.notes || null,
            video_url: input.videoUrl || null,
            hide_on_resume: input.hideOnResume || false,
            display_order: nextOrder,
          })
          .select()
          .single();

        if (error) {
          await logger.error(
            {
              name: 'add-to-repertoire-error',
              userId: user.id,
              error: error.message,
            },
            'Failed to add to repertoire',
          );

          return Result.fail(
            new ServiceError(
              'CREATE_ERROR',
              `Failed to add to repertoire: ${error.message}`,
              error,
            ),
          );
        }

        await logger.info(
          {
            name: 'add-to-repertoire-success',
            userId: user.id,
            repertoireId: data.id,
            pieceId: input.pieceId,
          },
          'Successfully added to repertoire',
        );

        const entry: DancerRepertoireEntry = {
          id: data.id,
          userId: data.user_id,
          pieceId: data.piece_id,
          performedAtOrganizationId: data.performed_at_organization_id,
          performedAtEventId: data.performed_at_event_id,
          performanceDate: data.performance_date,
          performanceLocation: data.performance_location,
          roleInPiece: data.role_in_piece,
          notes: data.notes,
          videoUrl: data.video_url,
          isVerified: data.is_verified ?? false,
          hideOnResume: data.hide_on_resume ?? false,
          displayOrder: data.display_order ?? 0,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        };

        return Result.ok(entry);
      } catch (err) {
        captureServiceError(
          { message: 'Failed to add to repertoire', code: 'CREATE_ERROR' },
          { action: 'addToRepertoire', step: 'insert' },
        );
        return Result.fail(
          new ServiceError(
            'CREATE_ERROR',
            `Failed to add to repertoire: ${err instanceof Error ? err.message : 'Unknown error'}`,
            err,
          ),
        );
      }
    },

    /**
     * Update a repertoire entry
     */
    async updateRepertoireEntry(
      entryId: string,
      input: UpdateRepertoireInput,
    ): Promise<Result<DancerRepertoireEntry>> {
      const logger = await getLogger();

      try {
        const {
          data: { user },
        } = await client.auth.getUser();

        if (!user) {
          return Result.fail(
            new ServiceError(
              'UNAUTHORIZED',
              'Must be logged in to update repertoire',
            ),
          );
        }

        const updateData: Database['public']['Tables']['artist_repertoire']['Update'] =
          {};

        if (input.performedAtOrganizationId !== undefined)
          updateData.performed_at_organization_id =
            input.performedAtOrganizationId;
        if (input.performedAtEventId !== undefined)
          updateData.performed_at_event_id = input.performedAtEventId;
        if (input.performanceDate !== undefined)
          updateData.performance_date = input.performanceDate;
        if (input.performanceLocation !== undefined)
          updateData.performance_location = input.performanceLocation;
        if (input.roleInPiece !== undefined)
          updateData.role_in_piece = input.roleInPiece;
        if (input.notes !== undefined) updateData.notes = input.notes;
        if (input.videoUrl !== undefined) updateData.video_url = input.videoUrl;
        if (input.hideOnResume !== undefined)
          updateData.hide_on_resume = input.hideOnResume;
        if (input.displayOrder !== undefined)
          updateData.display_order = input.displayOrder;

        const { data, error } = await client
          .from('artist_repertoire')
          .update(updateData)
          .eq('id', entryId)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) {
          await logger.error(
            {
              name: 'update-repertoire-error',
              userId: user.id,
              entryId,
              error: error.message,
            },
            'Failed to update repertoire entry',
          );

          return Result.fail(
            new ServiceError(
              'UPDATE_ERROR',
              `Failed to update repertoire: ${error.message}`,
              error,
            ),
          );
        }

        await logger.info(
          {
            name: 'update-repertoire-success',
            userId: user.id,
            entryId,
          },
          'Successfully updated repertoire entry',
        );

        const entry: DancerRepertoireEntry = {
          id: data.id,
          userId: data.user_id,
          pieceId: data.piece_id,
          performedAtOrganizationId: data.performed_at_organization_id,
          performedAtEventId: data.performed_at_event_id,
          performanceDate: data.performance_date,
          performanceLocation: data.performance_location,
          roleInPiece: data.role_in_piece,
          notes: data.notes,
          videoUrl: data.video_url,
          isVerified: data.is_verified ?? false,
          hideOnResume: data.hide_on_resume ?? false,
          displayOrder: data.display_order ?? 0,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        };

        return Result.ok(entry);
      } catch (err) {
        captureServiceError(
          { message: 'Failed to update repertoire', code: 'UPDATE_ERROR' },
          { action: 'updateRepertoireEntry', step: 'update' },
        );
        return Result.fail(
          new ServiceError(
            'UPDATE_ERROR',
            `Failed to update repertoire: ${err instanceof Error ? err.message : 'Unknown error'}`,
            err,
          ),
        );
      }
    },

    /**
     * Remove a piece from dancer's repertoire
     */
    async removeFromRepertoire(entryId: string): Promise<Result<null>> {
      const logger = await getLogger();

      try {
        const {
          data: { user },
        } = await client.auth.getUser();

        if (!user) {
          return Result.fail(
            new ServiceError(
              'UNAUTHORIZED',
              'Must be logged in to remove from repertoire',
            ),
          );
        }

        const { error } = await client
          .from('artist_repertoire')
          .delete()
          .eq('id', entryId)
          .eq('user_id', user.id);

        if (error) {
          await logger.error(
            {
              name: 'remove-from-repertoire-error',
              userId: user.id,
              entryId,
              error: error.message,
            },
            'Failed to remove from repertoire',
          );

          return Result.fail(
            new ServiceError(
              'DELETE_ERROR',
              `Failed to remove from repertoire: ${error.message}`,
              error,
            ),
          );
        }

        await logger.info(
          {
            name: 'remove-from-repertoire-success',
            userId: user.id,
            entryId,
          },
          'Successfully removed from repertoire',
        );

        return Result.ok(null);
      } catch (err) {
        captureServiceError(
          { message: 'Failed to remove from repertoire', code: 'DELETE_ERROR' },
          { action: 'removeFromRepertoire', step: 'delete' },
        );
        return Result.fail(
          new ServiceError(
            'DELETE_ERROR',
            `Failed to remove from repertoire: ${err instanceof Error ? err.message : 'Unknown error'}`,
            err,
          ),
        );
      }
    },
  };
}

export type DancerRepertoireService = ReturnType<
  typeof createDancerRepertoireService
>;
